import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BannerService } from '@app/core';
import { SHARED_MODULES } from '@app/shared';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash';
import { LocalStorageService } from 'ngx-webstorage';
import { interval, Observable, Subject } from 'rxjs';
import { filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { loadBanners, updateStorageBannersViewed } from '../../banner';

const BANNER_ROTATION_INTERVAL_MS = 6000;
const CAROUSEL_GAP_PX = 16;
const DRAG_ACTIVATION_THRESHOLD_PX = 6;
const DRAG_NAVIGATION_THRESHOLD_PX = 50;
const CLICK_SUPPRESSION_DELAY_MS = 50;

@Component({
  imports: [SHARED_MODULES],
  templateUrl: './modal-banners.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalBannersComponent implements OnInit, OnDestroy {
  protected readonly current = signal(0);
  protected readonly isDragging = signal(false);
  protected banners$!: Observable<any[]>;
  protected loading = signal(false);

  private readonly hasDragged = signal(false);
  private readonly _pause = signal(false);
  private readonly _destroy$ = new Subject<void>();
  private readonly _resetTimer$ = new Subject<void>();
  private readonly _carousel = viewChild<ElementRef<HTMLDivElement>>('carousel');
  private _startX = 0;
  private _scrollLeft = 0;
  private _dragDistanceX = 0;
  private readonly _destroy = inject(DestroyRef);
  private readonly _store = inject<Store<AppState>>(Store);
  private readonly _bannersService = inject(BannerService);
  private readonly _localStorageService = inject(LocalStorageService);

  public ngOnInit() {
    this.loading.set(true);
    this.banners$ = loadBanners(this._store, this._bannersService, this._destroy).pipe(
      tap(({ userId, banners }) => {
        this.loadListeners(banners);
        updateStorageBannersViewed(this._localStorageService, userId, banners);
      }),
      map(({ banners }) => banners),
      tap(() => this.loading.set(false)),
    );
  }

  public ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  protected onNavigate(index: number) {
    this.current.set(index);
    this._resetTimer$.next();
    const container = this._carousel()?.nativeElement;
    if (!container || !container.children.length) {
      return;
    }
    const children = container.children[0] as HTMLElement;
    container.scrollTo({
      left: (children.offsetWidth + CAROUSEL_GAP_PX) * index,
      behavior: 'smooth',
    });
  }

  protected onMouseDown(mouseEvent: MouseEvent, totalBanners: number) {
    if (totalBanners <= 1) {
      return;
    }
    this.isDragging.set(true);
    this.hasDragged.set(false);
    this.onLock();
    this._startX = mouseEvent.pageX;
    this._dragDistanceX = 0;
    this._scrollLeft = this._carousel()?.nativeElement.scrollLeft ?? 0;
  }

  protected onMouseMove(mouseEvent: MouseEvent) {
    if (!this.isDragging()) {
      return;
    }
    this._dragDistanceX = mouseEvent.pageX - this._startX;
    if (Math.abs(this._dragDistanceX) > DRAG_ACTIVATION_THRESHOLD_PX) {
      this.hasDragged.set(true);
    }
    const container = this._carousel()?.nativeElement;
    if (container) {
      container.scrollLeft = this._scrollLeft - this._dragDistanceX;
    }
  }

  protected onMouseUp(totalBanners: number) {
    if (!this.isDragging()) {
      return;
    }
    this.isDragging.set(false);
    this.onUnlock();

    if (!this.hasDragged()) {
      return;
    }

    const direction = this.getDragDirection();
    const targetIndex = Math.max(0, Math.min(this.current() + direction, totalBanners - 1));

    this.onNavigate(targetIndex);
    setTimeout(() => this.hasDragged.set(false), CLICK_SUPPRESSION_DELAY_MS);
  }

  protected onScroll(totalBanners: number) {
    if (this.isDragging()) {
      return;
    }
    const container = this._carousel()?.nativeElement;
    if (!container || !container.children.length) {
      return;
    }
    const child = container.children[0] as HTMLElement;
    const slideWidth = child.offsetWidth + CAROUSEL_GAP_PX;
    const index = Math.round(container.scrollLeft / slideWidth);
    const clampedIndex = Math.max(0, Math.min(index, totalBanners - 1));
    if (this.current() !== clampedIndex) {
      this.current.set(clampedIndex);
      this._resetTimer$.next();
    }
  }

  protected onLinkClick(mouseEvent: MouseEvent) {
    if (this.hasDragged()) {
      mouseEvent.preventDefault();
      mouseEvent.stopPropagation();
    }
  }

  protected onCardClick(mouseEvent: MouseEvent, index: number) {
    if (this.hasDragged()) {
      mouseEvent.preventDefault();
      mouseEvent.stopPropagation();
      return;
    }
    this.onNavigate(index);
  }

  protected onLock() {
    this._pause.set(true);
  }

  protected onUnlock() {
    this._pause.set(false);
  }

  private loadListeners(banners: any[]) {
    this._resetTimer$
      .pipe(
        startWith(null),
        switchMap(() => interval(BANNER_ROTATION_INTERVAL_MS)),
        takeUntil(this._destroy$),
        takeUntilDestroyed(this._destroy),
        filter(() => !(this._pause() || isEmpty(banners))),
      )
      .subscribe(() => this.onNavigate((this.current() + 1) % banners.length));
  }

  private getDragDirection(): number {
    if (this._dragDistanceX < -DRAG_NAVIGATION_THRESHOLD_PX) {
      return 1;
    }
    if (this._dragDistanceX > DRAG_NAVIGATION_THRESHOLD_PX) {
      return -1;
    }
    return 0;
  }
}
